<script setup="">
import { PlusIcon, XIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { VueDraggable } from "vue-draggable-plus";
import { object, string } from "yup";

import Alert from "@/Components/Alert.vue";
import InputError from "@/Components/Form/InputError.vue";
import SimpleInput from "@/Components/Form/SimpleInput.vue";
import SimpleSelect from "@/Components/Form/SimpleSelect.vue";
import ConfirmModal from "@/Components/Modal/ConfirmModal.vue";
import SimpleButton from "@/Components/SimpleButton.vue";

import { useValidator } from "@/composables/validator";
import { useList } from "@/utilities/listManager";
import { useForm } from "@inertiajs/vue3";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  update: {
    type: Function,
    required: true
  }
});

const selectedIndex = ref(-1);
const form = useForm({
  type: "text",
  name: "",
  value: ""
});

const schema = object().shape({
  name: string().required().label("텍스트"),
  type: string().required().oneOf(["text", "link"]).label("방식"),
  value: string().required().label("링크")
});

const validator = useValidator(schema);

const validate = () => validator.validateSync(form);

const list = useList(props.campaign?.settings?.footers ?? [], {
  max: 10,
  unique: true,
  key: (it) => it.type + it.name
});

const model = computed({
  get() {
    return list.toArray();
  },
  set(value) {
    list.clear();
    list.appendAll(value);

    props.update("settings.footers", list.toArray());
  }
});

const reset = () => {
  form.reset();
  validator.reset();
};

const append = ({ close }) => {
  list.put({
    type: form.type,
    name: form.name,
    value: form.value
  });

  props.update("settings.footers", list.toArray());

  reset();
  close();
};

const modify = (index) => {
  const element = list.get(index);
  form.type = element.type;
  form.name = element.name;
  form.value = element.value;
  selectedIndex.value = index;
};

const update = () => {
  const element = list.get(selectedIndex.value);
  element.type = form.type;
  element.name = form.name;
  element.value = form.value;

  props.update("settings.footers", list.toArray());
  reset();
  selectedIndex.value = -1;
};

list.on("changed", () => {
  props.update("settings.footers", list.toArray());
});
</script>

<template>
  <div>
    <div>
      <VueDraggable v-model="model" class="space-y-1">
        <div v-for="(item, index) in model" :key="index" class="cursor-pointer" @click="modify(index)">
          <div
            class="flex cursor-pointer items-center overflow-hidden rounded-xl border px-4 py-2 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            <dl class="min-w-0 flex-1 overflow-hidden truncate">
              <dt class="text-xs text-600">{{ item.name }}</dt>
              <dd class="text-sm dark:text-zinc-100">{{ item.value }}</dd>
            </dl>
            <div class="ml-2 w-5 flex-none" @click.stop>
              <XIcon class="h-5 w-5 cursor-pointer transition text-600 hover:text-red-500" @click="list.remove(index)" />
            </div>
          </div>
        </div>
      </VueDraggable>

      <div v-if="list.isEmpty()">
        <Alert classes="text-center text-sm text-600">버튼을 추가해 주세요.</Alert>
      </div>
      <!--end::empty-state-->

      <ConfirmModal
        :disabled="list.isMax()"
        title="버튼 추가"
        :validate="validate"
        @before-open="reset"
        @after-cose="selectedIndex = -1"
        @confirmed="append"
      >
        <template #button="{ open }">
          <div class="mt-5 w-full">
            <SimpleButton v-if="!list.isMax()" classes="w-full text-sm+" @click="open">
              <template #icon>
                <PlusIcon class="mr-1 h-4 w-4" />
              </template>
              항목 추가
            </SimpleButton>
            <p v-else class="text-sm text-600">더 이상 추가할 수 없습니다.</p>
          </div>
        </template>

        <template #content>
          <div class="space-y-4">
            <div>
              <SimpleSelect v-model="form.type" label="방식">
                <option value="text">일반</option>
                <option value="link">링크</option>
              </SimpleSelect>
              <InputError :message="validator.getFieldError('type')" />
            </div>

            <div>
              <SimpleInput v-model="form.name" label="항목" max-length="32" placeholder="예) 대표" />
              <InputError :message="validator.getFieldError('name')" />
            </div>

            <div>
              <SimpleInput v-model="form.value" label="값" max-length="256" placeholder="예) 홍길동" />
              <InputError :message="validator.getFieldError('value')" />
            </div>
          </div>
        </template>
      </ConfirmModal>
      <!--end::add-button-->

      <ConfirmModal
        :show="selectedIndex !== -1"
        title="항목 수정"
        :validate="validate"
        @before-close="reset"
        @after-close="selectedIndex = -1"
        @confirmed="update"
      >
        <template #content>
          <div class="space-y-4">
            <div>
              <SimpleSelect v-model="form.type" label="방식">
                <option value="text">일반</option>
                <option value="link">링크</option>
              </SimpleSelect>
              <InputError :message="validator.getFieldError('type')" />
            </div>

            <div>
              <SimpleInput v-model="form.name" label="항목" max-length="32" placeholder="예) 대표" />
              <InputError :message="validator.getFieldError('name')" />
            </div>

            <div>
              <SimpleInput v-model="form.value" label="값" max-length="256" placeholder="예) 홍길동" />
              <InputError :message="validator.getFieldError('value')" />
            </div>
          </div>
        </template>
      </ConfirmModal>
    </div>
  </div>
</template>
